import { request } from '@/functions/data-access';
import { SCView } from '@lib/sc-eone-ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ObjectDetailPanel } from './ObjectDetailPanel';
import { ObjectsPanel } from './ObjectsPanel';
import { SoqlPanel } from './SoqlPanel';
import { newGroup, type FilterGroup } from './filters';
import './metadata-browser.css';
import {
  MAX_PARENT_DEPTH,
  METADATA_BROWSER_TID as TID,
  type PathCrumb,
  type SObjectListItem,
  type SalesforceObjectMetadata,
  type SalesforceRelationship,
  type SubquerySelection,
} from './types';

/**
 * SC-741 Salesforce Metadata Browser — a read-only tool bound to a Salesforce connection, opened
 * from the Salesforce query data source editor. Renders inside the app shell (Layout), so the shell
 * supplies the header, menu, sidebars and theme. Three panels: Objects → Object detail
 * (Fields / Relationships) → SOQL query.
 *
 * Selection model: fields are stored as dot-paths relative to the base object. Drilling into a
 * parent reference pushes a crumb whose relationshipName extends the dot-path prefix, so selecting
 * a field on a drilled-in parent yields e.g. "Account.Owner.Email". Child-relationship subqueries
 * are a follow-up increment.
 */
export function MetadataBrowser() {
  const [searchParams] = useSearchParams();
  const connectionId = searchParams.get('connectionId') ?? '';

  const [path, setPath] = useState<PathCrumb[]>([]);
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
  // Child-relationship subqueries, keyed by relationship name. These belong to the base object
  // (SOQL allows only one level of subquery), so they persist while drilling into parents and
  // reset only when the base object changes.
  const [subqueries, setSubqueries] = useState<Record<string, SubquerySelection>>({});
  // WHERE filter tree (SC-741 §8), against the base object. Resets when the base object changes.
  const [where, setWhere] = useState<FilterGroup>(() => newGroup('AND'));

  const baseObject = path.length > 0 ? path[0].object : null;
  const currentObject = path.length > 0 ? path[path.length - 1].object : null;
  const prefix = path
    .slice(1)
    .map((p) => p.relationshipName)
    .join('.');
  // Once we've navigated through a child relationship, the SELECT dot-path is broken (you can't
  // dot-path through a child), so field selection is exploration-only from that point.
  const hasChildHop = path.slice(1).some((c) => c.isChild);
  const selectable = !hasChildHop;
  // "Parent depth" counts only parent hops (child hops are exploration, not dot-path).
  const parentDepth = path.slice(1).filter((c) => !c.isChild).length;
  // Subqueries can only be built against the base object's own child relationships.
  const canSubquery = path.length === 1;

  const { data: objects = [], isLoading: objectsLoading } = useQuery({
    queryKey: ['salesforce', 'metadata-browser', 'get-sobjects', connectionId],
    enabled: !!connectionId,
    queryFn: async () => {
      const res = await request<Array<{ Entity: string; Name: string; IsCustom: string }>>({
        url: `/salesforce/get-sobjects/${connectionId}`,
      });
      return res.map<SObjectListItem>((o) => ({
        entity: o.Entity,
        name: o.Name,
        isCustom: o.IsCustom === 'true',
      }));
    },
  });

  const { data: connectionName } = useQuery({
    queryKey: ['salesforce', 'metadata-browser', 'connection', connectionId],
    enabled: !!connectionId,
    queryFn: async () => {
      const res = await request<{ description?: string }>({
        url: `/connector-instance/${connectionId}`,
      });
      return res?.description ?? '';
    },
  });

  const { data: metadata, isLoading: metaLoading } = useQuery({
    queryKey: ['salesforce', 'metadata-browser', 'sobject-metadata', connectionId, currentObject],
    enabled: !!connectionId && !!currentObject,
    queryFn: () =>
      request<SalesforceObjectMetadata>({
        url: `/salesforce/get-sobject-metadata/${connectionId}/${currentObject}`,
      }),
  });

  const onSelectObject = (o: SObjectListItem) => {
    setPath([{ relationshipName: null, object: o.entity, label: o.name }]);
    setSelectedFields(new Set());
    setSubqueries({});
    setWhere(newGroup('AND'));
  };

  const onToggleSubqueryField = (relName: string, targetObject: string, fieldApiName: string) => {
    setSubqueries((prev) => {
      const current = prev[relName]?.fields ?? [];
      const nextFields = current.includes(fieldApiName)
        ? current.filter((f) => f !== fieldApiName)
        : [...current, fieldApiName];
      const next = { ...prev };
      if (nextFields.length === 0) delete next[relName];
      else next[relName] = { targetObject, fields: nextFields };
      return next;
    });
  };

  // Clear the whole SELECT/WHERE selection (keeps the current object/navigation).
  const onClearSelection = () => {
    setSelectedFields(new Set());
    setSubqueries({});
    setWhere(newGroup('AND'));
  };

  const onToggleField = (dotPath: string) => {
    setSelectedFields((prev) => {
      const next = new Set(prev);
      if (next.has(dotPath)) next.delete(dotPath);
      else next.add(dotPath);
      return next;
    });
  };

  const labelFor = (entity: string) => objects.find((o) => o.entity === entity)?.name ?? entity;

  const onNavigateParent = (r: SalesforceRelationship) => {
    if (!r.name) return;
    if (parentDepth >= MAX_PARENT_DEPTH) return;
    setPath((prev) => [
      ...prev,
      { relationshipName: r.name, object: r.targetObject, label: labelFor(r.targetObject) },
    ]);
  };

  // Navigate INTO a child relationship to explore its schema (browse-only — child data is queried
  // via a subquery from the base, not a dot-path). Capped to keep the breadcrumb sane.
  const onNavigateChild = (r: SalesforceRelationship) => {
    if (path.length >= 8) return;
    setPath((prev) => [
      ...prev,
      {
        relationshipName: r.name,
        object: r.targetObject,
        label: labelFor(r.targetObject),
        isChild: true,
      },
    ]);
  };

  const onCrumb = (index: number) => {
    setPath((prev) => (index >= prev.length - 1 ? prev : prev.slice(0, index + 1)));
  };

  const orderedFields = useMemo(() => [...selectedFields], [selectedFields]);

  if (!connectionId) {
    return (
      <SCView
        flexCol
        itemsCenter
        justifyCenter
        p={5}
        className="sfmb-root"
        DataTestId={`${TID}.no-connection`}
      >
        <SCView textDesc style={{ textAlign: 'center', maxWidth: 420 }}>
          No Salesforce connection specified. Open this tool from a Salesforce query data source
          (the <em>Metadata Browser</em> item in the left menu).
        </SCView>
      </SCView>
    );
  }

  return (
    <div className="sfmb-root" data-testid={TID}>
      {/* Slim page-context bar (the app header + menu come from Layout, since this now renders
          inside the shell). Shows which connection is being browsed. */}
      <SCView
        flex
        itemsCenter
        justifyBetween
        px={3}
        py={2}
        borderB
        className="sfmb-pagebar"
        style={{ flex: '0 0 auto' }}
      >
        <SCView flex itemsCenter gap={2}>
          <SCView fontBold textMd>
            Metadata Browser
          </SCView>
          <SCView textXs textDesc>
            Salesforce schema explorer &amp; SELECT builder
          </SCView>
        </SCView>
        <SCView flex itemsCenter gap={2}>
          <span className="sfmb-dot" />
          <SCView textXs textDesc>
            Read-only
          </SCView>
          {connectionName && (
            <SCView textXs className="sfmb-conn" title={connectionId}>
              {connectionName}
            </SCView>
          )}
        </SCView>
      </SCView>
      <div className="sfmb-body">
        <ObjectsPanel
          objects={objects}
          loading={objectsLoading}
          selected={baseObject}
          onSelect={onSelectObject}
        />
        <ObjectDetailPanel
          connectionId={connectionId}
          metadata={metadata}
          loading={metaLoading}
          path={path}
          prefix={prefix}
          parentDepth={parentDepth}
          selectable={selectable}
          selectedFields={selectedFields}
          onToggleField={onToggleField}
          onNavigateParent={onNavigateParent}
          onNavigateChild={onNavigateChild}
          onCrumb={onCrumb}
          canSubquery={canSubquery}
          subqueries={subqueries}
          onToggleSubqueryField={onToggleSubqueryField}
          where={where}
          onWhereChange={setWhere}
        />
        <SoqlPanel
          connectionId={connectionId}
          baseObject={baseObject}
          fields={orderedFields}
          subqueries={subqueries}
          where={where}
          onClear={onClearSelection}
        />
      </div>
    </div>
  );
}
